import React, { useCallback } from "react";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import { Project } from "../../../../types";
import { ProjectNavLink } from "../ProjectNavLink";
import { TeamNavLink } from "../TeamNavLink";
import DesignService from "../../../../service/DesignService";
import ProjectService from "../../../../service/ProjectService";
import { useProject } from "../../../../context/Project/useProject";
import { useTeam } from "../../../../context/Team/useTeam";

interface Props {
  canAddProject: boolean;
  id: string;
  name: string;
  projects: Project[];
  url: (projectId: string) => string;
}

export const TeamProject = (props: Props) => {
  const { projects, url, id, name } = props;
  const { t } = useTranslation();
  const { refetchProject } = useProject();
  const { refetchTeams, refetchTeam } = useTeam();

  const handleDesignDrop = useCallback(
    async (designId: string, targetProjectId: string) => {
      try {
        await DesignService.moveDesign({
          designId,
          newProjectId: targetProjectId,
        });

        toast.success(t("design.moved_success"));

        await refetchProject({ skipLoading: true });
      } catch (error) {
        console.error("Failed to move design:", error);
        toast.error(t("design.moved_error"));
      }
    },
    [refetchProject, t]
  );

  const handleProjectDrop = useCallback(
    async (projectId: string, targetTeamId: string) => {
      try {
        await ProjectService.moveProject(projectId, targetTeamId);

        toast.success(t("project.moved_success"));

        await refetchTeams({ skipLoading: true });
        refetchTeam({ skipLoading: true });
      } catch (error) {
        console.error("Failed to move project:", error);
        toast.error(t("project.moved_error"));
      }
    },
    [refetchTeams, t, refetchTeam]
  );

  return (
    <React.Fragment>
      <TeamNavLink {...props} onProjectDrop={handleProjectDrop}>
        {projects.map((project) => (
          <ProjectNavLink
            label={project.name}
            url={url(project.id)}
            key={project.id}
            projectId={project.id}
            teamId={id}
            teamName={name}
            onDesignDrop={handleDesignDrop}
          />
        ))}
      </TeamNavLink>
    </React.Fragment>
  );
};
